import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api/client';

/**
 * Query keys, centralised.
 *
 * A mutation has to invalidate the right caches, and a typo in an inline key
 * array fails silently — the screen just stops updating. Naming them once makes
 * that impossible.
 */
export const keys = {
  auth: ['auth'] as const,
  me: ['me'] as const,
  settings: ['settings'] as const,
  counts: ['tasks', 'counts'] as const,

  today: ['tasks', 'today'] as const,
  upcoming: (days?: number) => ['tasks', 'upcoming', days ?? null] as const,
  backlog: (offset: number) => ['tasks', 'backlog', offset] as const,
  completed: (offset: number) => ['tasks', 'completed', offset] as const,
  assignedToMe: (offset: number) => ['tasks', 'assigned-to-me', offset] as const,
  assignedByMe: (offset: number) => ['tasks', 'assigned-by-me', offset] as const,
  search: (q: string) => ['tasks', 'search', q] as const,

  task: (id: string) => ['tasks', 'detail', id] as const,
  attachments: (id: string) => ['tasks', id, 'attachments'] as const,
  comments: (id: string) => ['tasks', id, 'comments'] as const,
  activity: (id: string) => ['tasks', id, 'activity'] as const,

  labels: ['labels'] as const,
  byLabel: (labelId: string, offset: number) =>
    ['tasks', 'by-label', labelId, offset] as const,

  notifications: ['notifications'] as const,
  unreadCount: ['notifications', 'unread'] as const,

  userSearch: (q: string) => ['users', 'search', q] as const,
};

/** Every list of tasks, for blanket invalidation after a mutation. */
export const TASKS_ROOT = ['tasks'] as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Task data is small and changes for reasons this tab cannot see (a
      // delegate completing something). Short and refetch on focus.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry an auth failure or a permission error — the answer will
        // not change, and retrying a 401 just delays the redirect to sign-in.
        if (error instanceof ApiError && !error.isRetryable) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // A failed mutation is a user-visible action; it is rolled back and
      // surfaced rather than retried behind their back.
      retry: false,
    },
  },
});
